import React, { useEffect, useMemo, useState } from 'react';
import { authenticatedFetch } from '../utils/apiUtils';
import {
  PART_OF_SPEECH_OPTIONS,
  VERB_TYPE_OPTIONS,
  getVerbTypeFromPosCode,
} from '../utils/vocabularyLabels';
import '../styles/FormControls.css';
import './AddEditVocableScreen.css';

export interface AddEditVocableFormValues {
  lookupTerm: string;
  japanese: string;
  kana: string;
  english: string[];
  partOfSpeech: string;
  verbType?: string;
  isTransitive?: boolean;
}

export interface VocableDetails {
  id?: string;
  japanese: string;
  kana: string;
  english: string[];
  partOfSpeech?: string;
  verbType?: string;
  transitive?: boolean;
}

interface AddEditVocableScreenProps {
  onBack: () => void;
  onSave?: (
    values: AddEditVocableFormValues,
    options?: { addAnother?: boolean }
  ) => Promise<boolean> | boolean | void;
  initialVocable?: VocableDetails | null;
}

interface JMdictMatch {
  primaryKanji?: string;
  primaryReading?: string;
  meanings?: string[];
  partOfSpeech?: string;
  verb?: boolean;
  transitive?: boolean;
  isVerb?: boolean;
  isTransitive?: boolean;
  posCode?: string; // POS code for verb conjugation (e.g., "v1", "v5k", "vk")
}

function AddEditVocableScreen({ onBack, onSave, initialVocable }: AddEditVocableScreenProps) {
  const [lookupTerm, setLookupTerm] = useState(initialVocable?.japanese ?? '');
  const [japanese, setJapanese] = useState(initialVocable?.japanese ?? '');
  const [kana, setKana] = useState(initialVocable?.kana ?? '');
  const [englishText, setEnglishText] = useState(
    initialVocable?.english?.join(', ') ?? ''
  );
  const [partOfSpeech, setPartOfSpeech] = useState(
    initialVocable?.partOfSpeech ?? 'NOUN'
  );
  const [verbType, setVerbType] = useState(initialVocable?.verbType ?? 'GODAN');
  const [isTransitive, setIsTransitive] = useState(initialVocable?.transitive ?? false);
  const [isPrefilling, setIsPrefilling] = useState(false);
  const [prefillMessage, setPrefillMessage] = useState('');
  const [prefillMessageType, setPrefillMessageType] = useState<'error' | 'success' | ''>('');
  const [error, setError] = useState('');
  const [submitAction, setSubmitAction] = useState<'save' | 'saveAddAnother'>('save');

  const trimmedLookup = lookupTerm.trim();
  const hasFilledFields = [japanese, kana, englishText].some(
    (value) => value && value.trim().length > 0
  );
  const shouldShowClear = trimmedLookup.length === 0 && hasFilledFields;

  useEffect(() => {
    if (!initialVocable) {
      return;
    }
    setLookupTerm(initialVocable.japanese ?? '');
    setJapanese(initialVocable.japanese ?? '');
    setKana(initialVocable.kana ?? '');
    setEnglishText(initialVocable.english?.join(', ') ?? '');
    setPartOfSpeech(initialVocable.partOfSpeech ?? 'NOUN');
    setVerbType(initialVocable.verbType ?? 'GODAN');
    setIsTransitive(initialVocable.transitive ?? false);
  }, [initialVocable]);

  const isVerb = useMemo(() => partOfSpeech === 'VERB', [partOfSpeech]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    const parsedEnglish = englishText
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

    const payload: AddEditVocableFormValues = {
      lookupTerm,
      japanese,
      kana,
      english: parsedEnglish,
      partOfSpeech,
      verbType: isVerb ? verbType : undefined,
      isTransitive: isVerb ? isTransitive : undefined,
    };

    if (!onSave) {
      return;
    }

    const shouldAddAnother = submitAction === 'saveAddAnother';

    try {
      const result = await onSave(payload, { addAnother: shouldAddAnother });
      const wasSuccessful = typeof result === 'boolean' ? result : true;

      if (shouldAddAnother && wasSuccessful) {
        handleClear();
        setSubmitAction('save');
      }
    } catch (err) {
      // Set the specific error message from the exception
      // This will show the specific duplicate error message for 409 conflicts
      const errorMessage = err instanceof Error ? err.message : 'Failed to save vocable. Please try again.';
      setError(errorMessage);
    }
  };

  const normalizePartOfSpeech = (value?: string, isVerbMatch?: boolean): string => {
    if (isVerbMatch) {
      return 'VERB';
    }
    const upper = value?.toUpperCase();
    if (upper && PART_OF_SPEECH_OPTIONS.some((option) => option.value === upper)) {
      return upper;
    }
    return 'OTHER';
  };

  const handleClear = () => {
    setLookupTerm('');
    setJapanese('');
    setKana('');
    setEnglishText('');
    setPartOfSpeech('NOUN');
    setVerbType('GODAN');
    setIsTransitive(false);
    setPrefillMessage('');
    setPrefillMessageType('');
    setError('');
  };

  const handlePrefill = async () => {
    if (!trimmedLookup) {
      setPrefillMessage('Please enter a lookup term first.');
      setPrefillMessageType('error');
      return;
    }

    setIsPrefilling(true);
    setPrefillMessage('');
    setPrefillMessageType('');

    try {
      const response = await authenticatedFetch(
        `/api/vocabulary/find-matches?partial=${encodeURIComponent(trimmedLookup)}&maxResults=1`
      );

      if (!response.ok) {
        throw new Error('Dictionary lookup failed');
      }

      const matches: JMdictMatch[] = await response.json();

      if (!matches || matches.length === 0) {
        setPrefillMessage('No dictionary entries found for that term.');
        setPrefillMessageType('error');
        return;
      }

      const match = matches[0];
      const normalizedPos = normalizePartOfSpeech(
        match.partOfSpeech,
        match.isVerb ?? match.verb
      );

      const primaryKanji = match.primaryKanji ?? '';
      const primaryReading = match.primaryReading ?? '';
      const meanings = Array.isArray(match.meanings) ? match.meanings : [];

      if (primaryKanji) {
        setJapanese(primaryKanji);
      } else if (primaryReading) {
        // If no kanji is available, fill Japanese field with kana
        setJapanese(primaryReading);
      }
      if (primaryReading) {
        setKana(primaryReading);
      }
      if (meanings.length > 0) {
        setEnglishText(meanings.join(', '));
      }

      setPartOfSpeech(normalizedPos);
      const verbMatch = normalizedPos === 'VERB';
      if (verbMatch) {
        setIsTransitive(match.isTransitive ?? match.transitive ?? false);
        // Set verb type based on posCode if available
        if (match.posCode) {
          const mappedVerbType = getVerbTypeFromPosCode(match.posCode);
          setVerbType(mappedVerbType);
        }
      } else {
        setIsTransitive(false);
      }

    } catch (error) {
      setPrefillMessage(
        error instanceof Error ? error.message : 'Unable to prefill from dictionary.'
      );
      setPrefillMessageType('error');
    } finally {
      setIsPrefilling(false);
    }
  };

  return (
    <div className="add-edit-vocable-screen">
      <div className="add-edit-vocable-container">
        <div className="add-edit-vocable-header">
          <h2 className="add-edit-vocable-title">
            {initialVocable ? 'Edit Vocable' : 'Add Vocable'}
          </h2>
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        </div>

        <form className="add-edit-vocable-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="lookupTerm" className="form-label">
              Lookup term
            </label>
            <div className="lookup-row">
              <input
                id="lookupTerm"
                type="text"
                className="form-input"
                placeholder="e.g. 片付ける"
                value={lookupTerm}
                onChange={(event) => setLookupTerm(event.target.value)}
              />
              <button
                type="button"
                className="btn btn-secondary prefill-button"
                onClick={shouldShowClear ? handleClear : handlePrefill}
                disabled={!shouldShowClear && (isPrefilling || trimmedLookup.length === 0)}
              >
                {shouldShowClear ? 'Clear' : isPrefilling ? 'Prefilling...' : 'Prefill'}
              </button>
            </div>
            {prefillMessage && (
              <p
                className={`prefill-feedback ${
                  prefillMessageType === 'error' ? 'error' : 'success'
                }`}
              >
                {prefillMessage}
              </p>
            )}
            {prefillMessageType !== 'error' && (
              <p className="form-hint">
                This term will be used to search the dictionary and pre-fill the fields.
              </p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="japanese" className="form-label">
              Japanese (Kanji)
            </label>
            <input
              id="japanese"
              type="text"
              className="form-input"
              placeholder="Enter the Japanese word in kanji"
              value={japanese}
              onChange={(event) => setJapanese(event.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="kana" className="form-label">
              Kana (Reading)
            </label>
            <input
              id="kana"
              type="text"
              className="form-input"
              placeholder="Enter the reading in hiragana or katakana"
              value={kana}
              onChange={(event) => setKana(event.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="english" className="form-label">
              English meanings
            </label>
            <input
              id="english"
              type="text"
              className="form-input"
              placeholder="Separate multiple meanings with commas"
              value={englishText}
              onChange={(event) => setEnglishText(event.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="partOfSpeech" className="form-label">
              Part of speech
            </label>
            <select
              id="partOfSpeech"
              className="form-select"
              value={partOfSpeech}
              onChange={(event) => setPartOfSpeech(event.target.value)}
            >
              {PART_OF_SPEECH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {isVerb && (
            <>
              <div className="form-group">
                <label htmlFor="verbType" className="form-label">
                  Verb type
                </label>
                <select
                  id="verbType"
                  className="form-select"
                  value={verbType}
                  onChange={(event) => setVerbType(event.target.value)}
                >
                  {VERB_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group checkbox-group">
                <label htmlFor="isTransitive" className="checkbox-label">
                  <input
                    id="isTransitive"
                    type="checkbox"
                    checked={isTransitive}
                    onChange={(event) => setIsTransitive(event.target.checked)}
                  />
                  Transitive verb
                </label>
                <p className="form-hint">
                  Check if the verb takes a direct object.
                </p>
              </div>
            </>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="button-row">
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Cancel
            </button>
            {!initialVocable && (
              <button
                type="submit"
                className="btn btn-secondary"
                onClick={() => setSubmitAction('saveAddAnother')}
              >
                Save and Add
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              onClick={() => setSubmitAction('save')}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddEditVocableScreen;


